import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { Loader2, ChevronRight, ChevronDown, Building2, MapPin, Users, Factory, Table2, LineChart as LineChartIcon, ArrowUpRight, X } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const kg = (v) => (v == null ? "N/D" : Math.round(v).toLocaleString("es-MX"));
const money2 = (v) => (v == null ? "N/D" : `$ ${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const toM = (v) => (v == null ? "—" : `${(v / 1_000_000).toLocaleString("es-MX", { maximumFractionDigits: 1 })} M`);

function DetailModal({ panel, node, childLabel, children, onClose, onDrill }) {
  const kg = (v) => (v == null ? "N/D" : Math.round(v).toLocaleString("es-MX"));
  const colVal = (c) => {
    if (panel.col === "util_kg") return c.util_kg == null ? "N/D" : `$ ${Number(c.util_kg).toFixed(2)}`;
    if (panel.col === "hg") return c.hg == null ? "N/D" : `${c.hg} %`;
    return `${kg(c.kg)} kg`;
  };
  const dot = (c) => {
    const u = c.util_kg;
    return u == null ? "bg-slate-300" : u >= 2 ? "bg-emerald-500" : u > 0 ? "bg-amber-500" : "bg-red-500";
  };
  const colLabel = panel.col === "util_kg" ? "Util. $/kg" : panel.col === "hg" ? "HG %" : "Ventas";
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose} data-testid="dash-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <span className="inline-flex items-center bg-red-600 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded mr-2">{panel.title}</span>
            <span className="font-bold text-slate-800">{node.node_name}</span>
            <p className="text-sm text-slate-500 mt-1">Detalle agregado y desglose por {childLabel.toLowerCase()} · {node.month} {node.year}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="dash-detail-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-4 gap-2">
            {(panel.series || []).map((t, i) => (
              <div key={i} className={`rounded-lg px-2.5 py-2 ${t.current ? "border-2 border-red-500 bg-red-50" : "bg-slate-50"}`}>
                <p className="text-[9px] font-bold uppercase text-slate-400">{t.mes}</p>
                <p className={`text-[13px] font-bold tabular-nums ${t.current ? "text-red-600" : "text-slate-800"}`}>{t.v == null ? "N/D" : panel.fmt(t.v)}</p>
              </div>
            ))}
          </div>
          {children.length > 0 && (
            <>
              <p className="font-bold text-slate-800 mt-6 mb-2">Desglose por {childLabel.toLowerCase()}</p>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-[60px_1fr_140px] bg-slate-50 text-[10px] font-bold uppercase text-slate-400 px-4 py-2">
                  <span>Estado</span><span>{childLabel}</span><span className="text-right">{colLabel}</span>
                </div>
                {children.map((c) => (
                  <button key={c.id} onClick={() => !c.is_leaf && onDrill(c)} disabled={c.is_leaf} className="w-full grid grid-cols-[60px_1fr_140px] items-center px-4 py-3 border-t border-slate-100 hover:bg-slate-50 text-left disabled:cursor-default" data-testid={`dash-desglose-${c.name}`}>
                    <span><span className={`inline-block w-2.5 h-2.5 rounded-full ${dot(c)}`} /></span>
                    <span className="font-medium text-slate-800">{c.name}</span>
                    <span className="text-right tabular-nums font-semibold text-slate-900 flex items-center justify-end gap-1">{colVal(c)} {!c.is_leaf && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, head, series, fmt, mode, onOpen }) {
  const gid = `g-${title.replace(/[^a-zA-Z]/g, "")}`;
  const chartData = (series || []).map((t) => ({ mes: t.mes, v: t.v == null ? null : Number(t.v) }));
  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-5 ${series ? "cursor-pointer hover:shadow-md hover:border-red-300 transition-all" : ""}`} data-testid={`dash-panel-${title}`} onClick={series ? onOpen : undefined}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center bg-red-700 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded">{title}</span>
        {series && <ArrowUpRight className="w-4 h-4 text-slate-300" />}
      </div>
      <p className="font-bold text-slate-800 mt-3 mb-3">{head}</p>
      {!series ? (
        <div className="py-8 text-center text-sm text-slate-400">N/D · pendiente de fuente</div>
      ) : mode === "grafica" ? (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={chartData} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#dc2626" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="mes" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
            <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => fmt(v)} />
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Area type="monotone" dataKey="v" stroke="#dc2626" strokeWidth={2.5} fill={`url(#${gid})`} connectNulls dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {series.map((t, i) => (
            <div key={i} className={`rounded-lg px-2.5 py-2 ${t.current ? "border-2 border-red-500 bg-red-50" : "bg-slate-50"}`}>
              <p className="text-[9px] font-bold uppercase text-slate-400">{t.mes}</p>
              <p className={`text-[13px] font-bold tabular-nums ${t.current ? "text-red-600" : "text-slate-800"}`}>{t.v == null ? "N/D" : fmt(t.v)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="flex-1 min-w-[150px] px-5 py-4 border-r last:border-r-0 border-slate-200" data-testid={`dash-kpi-${label}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{value}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}

export default function EstructuraOrganizacional() {
  const { currentStructure, node, drillInto, goToPathIndex, selectEntireStructure, periodRange, loading: ctxLoading } = useGlobal();
  const [viewMode, setViewMode] = useState("datos");
  const [openPanel, setOpenPanel] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["dash-node", currentStructure?.key, node?.id ?? "root", periodRange?.start],
    queryFn: async () => (await api.get(`/dashboard/node`, {
      params: { structure: currentStructure.key, node_id: node?.id, fecha_inicio: periodRange.start },
    })).data,
    enabled: !!currentStructure && !ctxLoading,
  });

  if (ctxLoading || isLoading) return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>;
  if (!currentStructure) return <p className="text-center text-slate-400 py-32">No tienes estructuras organizacionales autorizadas.</p>;
  if (isError || !data) return <p className="text-center text-slate-400 py-32">Sin acceso a este nodo.</p>;

  const children = data.children || [];
  const childLabel = children[0]?.label || "nivel";

  return (
    <div className="max-w-[1500px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Resumen Ejecutivo · {data.structure_name}</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 tracking-tight">{data.node_name}</h1>
        <p className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-1.5">
          <button onClick={selectEntireStructure} className="hover:text-red-600" data-testid="dash-crumb-root">Grupo</button>
          {(node?.path || []).map((p, i) => (
            <span key={p.id} className="flex items-center gap-1.5">
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <button onClick={() => goToPathIndex(i)} className="hover:text-red-600">{p.name}</button>
            </span>
          ))}
          <span className="text-slate-300">·</span> Periodo {data.month} {data.year}
          {data.empresas ? <> · {data.empresas} empresas operativas</> : null}
        </p>
        </div>
        <div className="flex items-center bg-white border border-slate-200 rounded-xl p-1 shrink-0 shadow-sm">
          <button onClick={() => setViewMode("datos")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${viewMode === "datos" ? "bg-red-600 text-white" : "text-slate-500 hover:text-red-600"}`} data-testid="dash-view-datos">
            <Table2 className="w-3.5 h-3.5" /> Datos
          </button>
          <button onClick={() => setViewMode("grafica")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${viewMode === "grafica" ? "bg-red-600 text-white" : "text-slate-500 hover:text-red-600"}`} data-testid="dash-view-grafica">
            <LineChartIcon className="w-3.5 h-3.5" /> Gráficas
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="flex flex-wrap bg-white rounded-2xl border border-slate-200 shadow-sm">
        <Kpi label="Tendencia Ventas" value={kg(data.tendencia_ventas)} sub="kilos" />
        <Kpi label="Acumulado Año" value={kg(data.acumulado_anio)} sub="kilos" />
        <Kpi label="Vs Mes Anterior" value={data.momentum == null ? "N/D" : `${data.momentum} %`} sub="momentum" />
        <Kpi label="Utilidad Gestión" value={money2(data.utilidad_gestion)} sub="promedio $/kilo" />
        <Kpi label="HG Neto" value={data.hg_neto == null ? "N/D" : `${data.hg_neto} %`} sub="promedio inventario" />
        <Kpi label="Unidades Taller" value={data.unidades_taller == null ? "N/D" : data.unidades_taller} sub="flota total" />
      </div>

      {/* Panels: 6 métricas · 13 meses */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <Panel title="Ventas" head={`Tendencia: ${kg(data.tendencia_ventas)} kilos`} series={data.panels?.ventas} fmt={toM} mode={viewMode} onOpen={() => setOpenPanel({ title: "Ventas", series: data.panels?.ventas, fmt: toM, col: "kg" })} />
        <Panel title="ADR" head={`Utilidad de gestión: ${money2(data.utilidad_gestion)} por kilo`} series={data.panels?.util} fmt={money2} mode={viewMode} onOpen={() => setOpenPanel({ title: "ADR", series: data.panels?.util, fmt: money2, col: "util_kg" })} />
        <Panel title="Comisiones y Descuentos" head={`CyD: ${money2(data.panels?.cyd?.find((x) => x.current)?.v)} por kilo`} series={data.panels?.cyd} fmt={money2} mode={viewMode} onOpen={() => setOpenPanel({ title: "Comisiones y Descuentos", series: data.panels?.cyd, fmt: money2, col: "kg" })} />
        <Panel title="Inventarios" head={`HG Neto: ${data.hg_neto == null ? "N/D" : data.hg_neto + " %"}`} series={data.panels?.hg} fmt={(v) => (v == null ? "N/D" : `${v}%`)} mode={viewMode} onOpen={() => setOpenPanel({ title: "Inventarios", series: data.panels?.hg, fmt: (v) => (v == null ? "N/D" : `${v}%`), col: "hg" })} />
        <Panel title="Taller" head="Sin fuente de datos" series={null} fmt={(v) => v} mode={viewMode} />
        <Panel title="Presupuesto Planta" head={`Costo: ${money2(data.panels?.costo?.find((x) => x.current)?.v)} por kilo`} series={data.panels?.costo} fmt={money2} mode={viewMode} onOpen={() => setOpenPanel({ title: "Presupuesto Planta", series: data.panels?.costo, fmt: money2, col: "kg" })} />
      </div>

      {openPanel && (
        <DetailModal panel={openPanel} node={data} childLabel={childLabel} children={children} onClose={() => setOpenPanel(null)} onDrill={(c) => { setOpenPanel(null); drillInto(c); }} />
      )}

      {/* Children cards */}
      {children.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-bold text-slate-800 mb-4">{childLabel}s <span className="text-sm font-normal text-slate-400">— selecciona para explorar el detalle</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {children.map((c) => (
              <button key={c.id} onClick={() => drillInto(c)} disabled={c.is_leaf} className="text-left bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:border-red-400 hover:shadow-md transition-all disabled:cursor-default" data-testid={`dash-card-${c.name}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{c.label}</p>
                    <p className="font-display font-bold text-slate-900 text-lg leading-tight">{c.name}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 py-3 border-y border-slate-100">
                  <div className="text-center"><MapPin className="w-4 h-4 mx-auto text-slate-400" /><p className="text-lg font-bold text-slate-900 mt-1">{c.zonas}</p><p className="text-[9px] uppercase text-slate-400">Zonas</p></div>
                  <div className="text-center"><Users className="w-4 h-4 mx-auto text-slate-400" /><p className="text-lg font-bold text-slate-900 mt-1">{c.regionales}</p><p className="text-[9px] uppercase text-slate-400">Regionales</p></div>
                  <div className="text-center"><Factory className="w-4 h-4 mx-auto text-slate-400" /><p className="text-lg font-bold text-slate-900 mt-1">{c.empresas}</p><p className="text-[9px] uppercase text-slate-400">Empresas</p></div>
                </div>
                <div className="flex items-end justify-between mt-3">
                  <div><p className="text-[10px] uppercase text-slate-400">Tendencia Ventas</p><p className="font-bold text-slate-900 tabular-nums">{kg(c.kg)} <span className="text-xs font-normal text-slate-400">kg</span></p></div>
                  <div className="text-right"><p className="text-[10px] uppercase text-slate-400">Util. $/kg</p><p className="font-bold text-red-600 tabular-nums">{money2(c.util_kg)}</p></div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
